import { Button, cn } from '@crm/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { LandingAccent } from '@/modules/landing/api';

const ACCENT_PREVIEWS: Record<LandingAccent, { label: string; hero: string; soft: string }> = {
  VIOLET: { label: 'Violeta', hero: 'bg-violet-600', soft: 'bg-violet-100' },
  ROSE: { label: 'Rose', hero: 'bg-rose-600', soft: 'bg-rose-100' },
  EMERALD: { label: 'Esmeralda', hero: 'bg-emerald-600', soft: 'bg-emerald-100' },
  AMBER: { label: 'Ambar', hero: 'bg-amber-600', soft: 'bg-amber-100' },
  SLATE: { label: 'Grafite', hero: 'bg-slate-800', soft: 'bg-slate-100' },
};

const ORDEM: LandingAccent[] = ['VIOLET', 'ROSE', 'EMERALD', 'AMBER', 'SLATE'];

interface StylePickerProps {
  value: LandingAccent;
  onChange: (value: LandingAccent) => void;
}

/**
 * Escolha do estilo da landing. Do sm em diante, formato "peek" (central
 * grande, vizinhos espiando nas bordas) - igual o picker de foto da
 * categoria. No celular isso nao cabe direito: so a miniatura atual, largura
 * toda, com Anterior/Proximo embaixo.
 */
export function StylePicker({ value, onChange }: StylePickerProps) {
  const total = ORDEM.length;
  const indice = Math.max(0, ORDEM.indexOf(value));
  const anterior = ORDEM[(indice - 1 + total) % total] as LandingAccent;
  const proxima = ORDEM[(indice + 1) % total] as LandingAccent;

  return (
    <div className="space-y-2 text-center">
      {/* Celular: miniatura principal em largura total, altura proporcional. */}
      <div className="space-y-2 sm:hidden">
        <div className="aspect-[4/3] w-full overflow-hidden rounded-md border border-border shadow-md">
          <MiniLanding accent={value} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={() => onChange(anterior)}>
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <Button type="button" variant="outline" onClick={() => onChange(proxima)}>
            Próximo
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Do sm em diante: carrossel "peek", com os vizinhos espiando nas bordas. */}
      <div className="relative hidden h-44 w-full items-center justify-center sm:flex">
        <div className="absolute inset-y-0 left-0 w-[16%] overflow-hidden rounded-l-md">
          <MiniLanding accent={anterior} />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-background" />
        </div>
        <div className="absolute inset-y-0 right-0 w-[16%] overflow-hidden rounded-r-md">
          <MiniLanding accent={proxima} />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        </div>

        <div className="relative z-10 h-full w-1/2 overflow-hidden rounded-md border border-border shadow-md">
          <MiniLanding accent={value} />
        </div>

        <button
          type="button"
          aria-label="Estilo anterior"
          onClick={() => onChange(anterior)}
          className="-translate-y-1/2 absolute left-1 top-1/2 z-20 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)] hover:opacity-80"
        >
          <ChevronLeft className="h-14 w-14" strokeWidth={2.5} />
        </button>
        <button
          type="button"
          aria-label="Proximo estilo"
          onClick={() => onChange(proxima)}
          className="-translate-y-1/2 absolute right-1 top-1/2 z-20 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)] hover:opacity-80"
        >
          <ChevronRight className="h-14 w-14" strokeWidth={2.5} />
        </button>
      </div>
      <p className="text-sm font-medium">{ACCENT_PREVIEWS[value].label}</p>
    </div>
  );
}

/**
 * Miniatura da landing de verdade: cabecalho fixo (logo + botao de agendar),
 * a foto do topo com gradiente e o titulo por cima, as duas saidas lado a
 * lado (Agendar / Atendente) e a vitrine de categorias - na mesma ordem da
 * pagina publica de verdade (`salon-page.tsx`), so que em miniatura.
 */
function MiniLanding({ accent }: { accent: LandingAccent }) {
  const preview = ACCENT_PREVIEWS[accent];

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-card text-left">
      <div className="flex shrink-0 items-center justify-between gap-1 border-b border-border/60 px-2 py-1.5">
        <div className="flex min-w-0 items-center gap-1">
          <span className={cn('h-2.5 w-2.5 shrink-0 rounded-sm', preview.hero)} />
          <span className="h-1.5 w-9 rounded-full bg-muted-foreground/30" />
        </div>
        <span className={cn('h-3 w-8 shrink-0 rounded-full', preview.hero)} />
      </div>

      <div
        className={cn('relative flex h-14 shrink-0 flex-col justify-end gap-1 p-2', preview.hero)}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <span className="relative h-1 w-8 rounded-full bg-white/70" />
        <span className="relative h-1.5 w-16 rounded-full bg-white" />
      </div>

      <div className="flex shrink-0 gap-1 px-2 py-1.5">
        <span className={cn('h-3 flex-1 rounded-full', preview.hero)} />
        <span className="h-3 flex-1 rounded-full border border-border" />
      </div>

      <div className="flex flex-1 items-end gap-1 px-2 pb-2">
        {[0, 1, 2].map((item) => (
          <div
            key={item}
            className={cn('relative h-full flex-1 overflow-hidden rounded', preview.soft)}
          >
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        ))}
      </div>
    </div>
  );
}
